import {useMutation} from '@apollo/react-hooks'
import gql from 'graphql-tag'
import {useState} from 'react'
import {Trans} from 'react-i18next'
import {useParams} from 'react-router-dom'
import messages from '../../../../../assets/messages'
import Loading from '../../../../Common/Loading'
import {PammNetworkTree} from '../../../../PammNetworkTree'
import {usePammNetworkOffers} from '../../../../PammNetworkTree/usePammNetworkOffers'
import {EmptyState} from '../../../../PammOffersTree/PammOffersTree.styled'
import {getOfferChainIds} from '../../../../PammOffersTree/utils/buildOfferTree'
import {AUTO_CREATED_OFFER_NAME} from '../../../../PammOffersTree/utils/offerDisplayName'
import {Container} from './components/Container'
import {InvestSelfBanner} from './components/InvestSelfBanner'

export const SET_PAMM_MAIN_OFFER = gql`
  mutation SetPammMainOffer($accountId: Int!, $offerId: Int) {
    setPammMainOffer(accountId: $accountId, offerId: $offerId) {
      ... on BaseAccount {
        id
        pammMainOfferId
      }
    }
  }
`

/**
 * Money Manager view of their PAMM offers (reached via a PAMM account tile -> "View Offers").
 *
 * Renders the manager's offer together with every nested agent / sub-agent offer as a
 * hierarchical, expandable tree (PT-5399). The page opens on the master view; a manager who also
 * holds an agent position elsewhere can switch to that scoped perspective from the toggle (PT-5717).
 */
export const PammManagerOffers = () => {
  const {accountId} = useParams<{accountId: string}>()
  const managerAccountId = Number(accountId)
  const {agentOffers, deniedTree, error, loading, mainOfferId, masterOffers} =
    usePammNetworkOffers(managerAccountId)
  const [saveFailed, setSaveFailed] = useState(false)
  const [setMainOffer, {loading: saving}] = useMutation(SET_PAMM_MAIN_OFFER, {
    // The mutation returns the new value, so the cached account updates itself. The refetch is not
    // awaited: a failed refetch after a stored choice must not read as a failed save.
    refetchQueries: ['AccountsWithPammNetworkOffers'],
  })

  const onMainOfferChange = async (offerId: number | null) => {
    setSaveFailed(false)
    try {
      await setMainOffer({variables: {accountId: managerAccountId, offerId}})
    } catch (e) {
      // The server refuses an offer that is not a root of this manager; the stored choice stays as it was.
      setSaveFailed(true)
    }
  }

  // This route takes the account id from the URL, so it can be pointed at an account whose Money
  // Manager the viewer does not own. The server is what refuses the tree (PT-5720) — this branch
  // only decides what the refusal looks like, and exists so that path reads as an answer rather
  // than as the blank area a bare `return null` would leave behind. Checked ahead of `error` so a
  // refusal reads as one whether or not the hook also reports an error alongside it.
  if (deniedTree) {
    return (
      <Container>
        <EmptyState data-testid="pamm-manager-offers-denied">
          <Trans {...messages.pammManagerTreeNoAccess} />
        </EmptyState>
      </Container>
    )
  }

  if (error) return null
  if (loading) return <Loading />

  // The auto-created "Default" offer is internal, but it is also the manager's own root: it is the
  // chain-less offer every agent chain of length 1 hangs off. Dropping it before the tree was built
  // handed the whole agent level to whichever other chain-less offer happened to have the lowest id,
  // so an agent who brought nobody appeared to own the manager's network (PT-5875). It earns its
  // place only while there is such a network: with no agent chain anywhere the manager's own offers
  // have nothing to hang off it, and it renders as a second card the manager never created and as a
  // sub-agent in the KPI row that does not exist (PT-6293). Keep it exactly when some offer carries
  // an agent chain; otherwise the manager's own offers are the roots. The one exception is a Default
  // offer the manager marked as Main Offer (PT-6881): hidden, it would keep a choice that no card shows
  // and no button can clear.
  const hasAgentOffers = masterOffers.some((offer) => getOfferChainIds(offer).length > 0)
  const visibleOffers = hasAgentOffers
    ? masterOffers
    : masterOffers.filter((offer) => offer.name !== AUTO_CREATED_OFFER_NAME || offer.id === mainOfferId)

  return (
    <Container>
      <InvestSelfBanner pammManagerAccountId={accountId} />
      {saveFailed && (
        <EmptyState data-testid="pamm-main-offer-save-error" role="alert">
          <Trans {...messages.pammMainOfferSaveError} />
        </EmptyState>
      )}
      <PammNetworkTree
        masterOffers={visibleOffers}
        agentOffers={agentOffers}
        defaultView="master"
        mainOffer={{offerId: mainOfferId, saving, onChange: onMainOfferChange}}
      />
    </Container>
  )
}
